import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  QueryId,
  TOTAL_PRICE_DEPENDENT_QUERIES,
} from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseMutationDeletePreBookingPickupPointData =
  Booking.Components['PutPickupPointResponse'];

interface UseMutationDeletePreBookingPickupPointError {
  errors: any;
  message?: string;
}

interface UseMutationDeletePreBookingPickupPointParameters {
  brand?: Content.Properties.Brand;
  data: Booking.Components['UniquePaxHashesBodyRequest'];
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
}

const useMutationDeletePreBookingPickupPoint = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    UseMutationDeletePreBookingPickupPointData,
    UseMutationDeletePreBookingPickupPointError,
    UseMutationDeletePreBookingPickupPointParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/pickup-point/delete-pickup-point',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            data: parameters.data,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'DELETE',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error(
            'bookingFlow.useMutationDeletePreBookingPickupPoint',
            error,
          );

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (_data, parameters) => {
        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });

        TOTAL_PRICE_DEPENDENT_QUERIES.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationDeletePreBookingPickupPoint;
